"use client";

import styles from "./SignUpAccountDetailsStep.module.scss";
import classNames from "classnames";
import { useForm, useGoogleAuth } from "@/hooks";
import {
  setFieldError,
  setFieldValue } from "@/stores";
import { GoogleSignInButton } from "@/components";
import { isGoogleOAuthConfigured } from "@/constants";

import { useLocale, useTranslations } from "@/i18n";

import { useAccountDetailsForm } from "../../_hooks";
import {
  Checkbox,
  Fieldset,
  Button,
  Input,
  Flex,
} from "@vetty/ui";

/**
 * SignUpAccountDetailsStep Component
 *
 * Second step of the sign-up process where users enter their account details.
 * Handles form validation, email/phone availability checks, and phone verification.
 */
export const SignUpAccountDetailsStep = () => {
  const t = useTranslations("signUp.form");
  const tConsents = useTranslations("signUp.consents");
  const locale = useLocale();
  const googleEnabled = isGoogleOAuthConfigured();

  // Google OAuth state for loading management
  const googleAuthState = useGoogleAuth();

  // Form state and submission logic
  const { errors, values, loading } = useForm();

  // Account details form state and submission logic
  const { handleInputChange, handleFormSubmit } = useAccountDetailsForm();

  // Terms & privacy href
  const termsHref = (path: string) => `https://vetty3d.com/${locale}/${path}`;

  // Terms & privacy change handler
  const handleTermsChange = (checked: boolean) => {
    setFieldValue("termsAccepted", checked);
    setFieldError("termsAccepted", "");
    setFieldValue("termsAcceptedAt", checked ? new Date().toISOString() : "");
  };

  return (
    <Flex direction={"column"} className={styles["comp"]}>
      {googleEnabled && <GoogleSignInButton />}

      {googleEnabled && (
        <Flex className={styles["comp__or"]} align={"center"} gap={8}>
          <div className={styles["comp__or__line"]}></div>
          <div className={styles["comp__or__label"]}>{t("or")}</div>
          <div className={styles["comp__or__line"]}></div>
        </Flex>
      )}

      {/* Account details form */}
      <Flex
        direction={"column"}
        gap={20}
        className={classNames(
          styles["comp__content"],
          googleAuthState.loading && styles["comp__content--disabled"],
        )}
      >
        {/* First name and last name inputs */}
        <Flex gap={16}>
          <Fieldset isError={errors.firstName} errorMessage={errors.firstName}>
            <Input
              id={"firstName"}
              label={t("firstName")}
              defaultValue={String(values.firstName ?? "")}
              onChange={(v) => handleInputChange("firstName", v)}
            />
          </Fieldset>

          <Fieldset isError={errors.lastName} errorMessage={errors.lastName}>
            <Input
              id={"lastName"}
              label={t("lastName")}
              defaultValue={String(values.lastName ?? "")}
              onChange={(v) => handleInputChange("lastName", v)}
            />
          </Fieldset>
        </Flex>

        {/* E-mail input */}
        <Fieldset isError={errors.email} errorMessage={errors.email}>
          <Input
            id={"email"}
            label={t("email")}
            defaultValue={String(values.email ?? "")}
            onChange={(v) => handleInputChange("email", v)}
          />
        </Fieldset>

        {/* Phone number input */}
        {/* <Fieldset
          isError={errors.phoneNumber}
          errorMessage={errors.phoneNumber}
        >
          <PhoneNumberInput
            id={"phoneNumber"}
            label={t("phoneNumber")}
            defaultValue={values.phoneNumber}
            onChange={(v) => handleInputChange("phoneNumber", v)}
          />
        </Fieldset> */}

        {/* Submit button */}
        <Fieldset
          className={styles["comp__submit"]}
          isError={errors.general}
          errorMessage={errors.general}
        >
          <Button
            disabled={loading}
            loading={loading}
            onClick={() => handleFormSubmit(values)}
            className={styles["comp__submit-button"]}
          >
            {t("continue")}
          </Button>
        </Fieldset>

        {/* Terms & privacy — required checkbox */}
        <Fieldset
          className={styles["comp__consents"]}
          isError={errors.termsAccepted}
          errorMessage={errors.termsAccepted}
        >
          <Checkbox
            id={"sign-up-terms-accepted"}
            checked={values.termsAccepted === true}
            onChange={handleTermsChange}
          >
            {tConsents.rich("termsAndPrivacyRich", {
              terms: (chunks) => (
                <a
                  href={termsHref("terms")}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  className={styles["comp__consents-link"]}
                >
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a
                  href={termsHref("privacy")}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  className={styles["comp__consents-link"]}
                >
                  {chunks}
                </a>
              ),
            })}
          </Checkbox>
        </Fieldset>
      </Flex>
    </Flex>
  );
};
